import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { SlidersHorizontal, Calculator } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
export function SlideValueCalculator({}: SlideProps) {
  const [revenueB, setRevenueB] = useState(32);
  const [legacyPercent, setLegacyPercent] = useState(0);
  const [improvementPercent, setImprovementPercent] = useState(0.5);
  const [animationDone, setAnimationDone] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const timers = [setTimeout(() => setLegacyPercent(13.5), 1000), setTimeout(() => setImprovementPercent(1), 1800), setTimeout(() => setAnimationDone(true), 2800)];
    return () => timers.forEach(clearTimeout);
  }, []);
  const upside = revenueB * 1000 * (legacyPercent / 100) * (improvementPercent / 100);
  const upsideValue = useSpring(0, {
    bounce: 0,
    duration: 1600
  });
  const displayUpside = useTransform(upsideValue, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    if (revealed) upsideValue.set(upside);
  }, [revealed, upside, upsideValue]);
  const handleChange = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInteracted(true);
    setter(Number(e.target.value));
  };
  const sliders = [{
    label: 'Group Revenue',
    value: `€${revenueB.toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: 5,
    max: 65,
    step: 0.5,
    current: revenueB,
    onChange: handleChange(setRevenueB),
    footer: ['€5bn', 'Illustrative: €32bn', '€65bn']
  }, {
    label: '% from Established Medicines',
    value: `€${(revenueB * legacyPercent / 100).toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: 5,
    max: 40,
    step: 0.5,
    current: legacyPercent,
    onChange: handleChange(setLegacyPercent),
    footer: ['5%', `${legacyPercent.toFixed(1)}% — est. 12–15%`, '40%']
  }, {
    label: 'Target Improvement',
    value: `${improvementPercent.toFixed(1)}%`,
    valueClass: 'text-[var(--brand)]',
    min: 0.5,
    max: 5,
    step: 0.5,
    current: improvementPercent,
    onChange: handleChange(setImprovementPercent),
    footer: ['0.5%', '', '5%']
  }];
  const rationale = "In an illustrative large-portfolio scenario, 12–15% of global revenue may still come from established medicines — potentially €4–5bn annually — even as commercial resources shift toward new and specialty products.";
  return <InteriorSlide data-id="element-406" data-mp="22xb4oalsgqg2">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="mb-[clamp(1rem,2vw,2rem)] flex-shrink-0" data-id="element-407" data-mp="22yvx9uojlzba">
        <div className="flex items-center gap-3 mb-3" data-id="element-408" data-mp="1lnqpsqdyaai0">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-409" data-mp="1lmfocx7z9eu2">
            Illustrative model
          </span>
          <span className="h-px flex-1 bg-black/10 max-w-[6rem]" data-id="element-410" data-mp="1velb9sjdh62e" />
          <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-411" data-mp="1qnlfwrpwiw20">
            Established portfolio
          </span>
        </div>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.6rem,3vw,2.75rem)] leading-[1.1] tracking-tight max-w-5xl" data-id="element-412" data-mp="24doucwijhlgw">
          Even a{' '}
          <span className="text-[var(--brand)]" data-id="element-413" data-mp="2ed5z9bpyvvbt">
            {improvementPercent.toFixed(1)}%
          </span>{' '}
          improvement across an established portfolio =
        </h2>
      </motion.div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center" data-id="element-414" data-mp="jz500pqi96b7">
        <div className="lg:col-span-5 relative rounded-3xl border border-black/5 bg-[var(--surface)] p-[clamp(1.25rem,2vw,2rem)] space-y-6" data-id="element-415" data-mp="f0bnp0myflcm">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: animationDone && !userInteracted ? 1 : 0
        }} className="absolute -top-11 left-0 right-0 flex items-center justify-center" data-id="element-416" data-mp="ggfraa1s6td1">
            <motion.div animate={{
            y: [0, -4, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.5
          }} className="flex items-center gap-2 rounded-full border border-[var(--brand)]/30 bg-[var(--brand)]/10 px-4 py-2 text-sm font-medium text-[var(--brand)]" data-id="element-417" data-mp="vdgk6gvpkud3">
              <SlidersHorizontal className="h-4 w-4" data-id="element-418" data-mp="cfvs70xndfo9" />
              Adjust the sliders — set your own numbers
            </motion.div>
          </motion.div>

          {sliders.map(s => <div key={s.label} className="space-y-2.5" data-id="element-419" data-mp="3g47c13wzncq5">
              <div className="flex justify-between items-end gap-4" data-id="element-420" data-mp="3g2md723n57mh">
                <label htmlFor={s.label} className="text-[var(--fg)] font-semibold text-sm" data-id="element-421" data-mp="1x8b8us2cgxlp">
                  {s.label}
                </label>
                <span className={`text-2xl font-bold ${s.valueClass}`} data-id="element-422" data-mp="1o7mozgq9s30w">
                  {s.value}
                </span>
              </div>
              <input id={s.label} type="range" min={s.min} max={s.max} step={s.step} value={s.current} onChange={s.onChange} className="deck-slider w-full" data-id="element-423" data-mp="1jc7ij9uld9ei" />
              <div className="flex justify-between text-xs text-[var(--muted)]" data-id="element-424" data-mp="10jbwp1yn3bo8">
                <span data-id="element-425" data-mp="10h8xpsfie87d">{s.footer[0]}</span>
                <span className="text-[var(--brand)]" data-id="element-426" data-mp="vks8euwkpnsp">{s.footer[1]}</span>
                <span data-id="element-427" data-mp="ql5wcoshedlh">{s.footer[2]}</span>
              </div>
            </div>)}
        </div>

        <div className="lg:col-span-7 flex flex-col justify-center" data-id="element-428" data-mp="gxjtbj058ybg">
          {revealed ? <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="space-y-4" data-id="element-429" data-mp="lwmxz2c7p2iw">
              <h3 className="text-[var(--muted)] text-base font-semibold uppercase tracking-[0.2em]" data-id="element-430" data-mp="zqdy5k41za1y">
                Unlocked Value
              </h3>
              <div className="flex items-baseline gap-2 text-[var(--brand)]" data-id="element-431" data-mp="quojrv10vfy2">
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-432" data-mp="uwt6e9t1zcb7">€</span>
                <motion.span className="text-[clamp(3.5rem,8vw,7rem)] font-bold tracking-tight leading-none" data-id="element-433" data-mp="1in5pmgtr0vh3">
                  {displayUpside}
                </motion.span>
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-434" data-mp="1nfqhvdx0b0ii">m</span>
              </div>
              <p className="text-[var(--muted)] text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed border-l-2 border-[var(--brand)]/40 pl-4 py-1 max-w-xl" data-id="element-435" data-mp="381ycd5yu8dtj">
                {rationale}
              </p>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="space-y-6 max-w-xl" data-id="element-436" data-mp="3cuhm1ruemot2">
              <h3 className="text-[var(--muted)] text-base font-semibold uppercase tracking-[0.2em]" data-id="element-437" data-mp="2u3qhd3ld3ml0">
                Unlocked Value
              </h3>
              <p className="text-[var(--muted)] text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed" data-id="element-438" data-mp="ebaqe1seg4e1">
                {rationale}
              </p>
              <motion.button type="button" onClick={() => setRevealed(true)} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} animate={{
            opacity: animationDone ? 1 : 0.4
          }} className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-8 py-4 text-lg font-bold text-white shadow-lg shadow-[var(--brand)]/25 transition-colors hover:bg-[var(--brand-light)]" data-id="element-439" data-mp="22x9stabw2egt">
                <Calculator className="h-5 w-5" data-id="element-440" data-mp="2vlovcxdu34b9" />
                Calculate potential
              </motion.button>
            </motion.div>}
        </div>
      </div>
    </InteriorSlide>;
}